/**
 * Event bridges for Warp web components (`<w-select>`, `<w-radio>`, `<w-checkbox>`).
 *
 * React's synthetic `onChange`/`onBlur` never fire on custom elements, so these helpers attach
 * the element's own native `change`/`focusout` listeners and expose the result as a callback ref
 * (`focusout` bubbles across the shadow boundary; `blur` does not). Kept framework-agnostic — a
 * local `RefFn` type, no React import — so it belongs in `@finn-no/tjt-common-utils`; consumers
 * pass the returned function as a React `ref`. React 19 runs the returned cleanup on unmount / ref
 * change.
 */

type RefFn = (el: HTMLElement | null) => void;

/**
 * Bridge a `<w-select>` to a react-hook-form `<Controller>` (the controlled pattern).
 *
 * `register()`-based required validation does NOT fire for a custom element — RHF validates
 * through the native DOM input a `<w-select>` doesn't expose — so fields that must be required
 * use `<Controller>`, which validates its own value. This bridges the element's native `change`
 * to the controller's value callback (the `change` CustomEvent's `detail` is the new value; the
 * host `.value` isn't updated by it, so copy it over for display) and `focusout` to onBlur, and
 * forwards the controller `ref`. Ref identity may change per render — harmless here, because
 * `<Controller>` validates its value, not the ref.
 */
export const bindSelect =
    ({
        onValueChange,
        onBlur,
        ref,
    }: {
        onValueChange: (value: string) => void;
        onBlur?: () => void;
        ref?: RefFn;
    }) =>
    (el: HTMLElement | null) => {
        ref?.(el);
        if (!el) return;
        const select = el as HTMLSelectElement;
        const handleChange = (event: Event) => {
            const { detail } = event as CustomEvent<string>;
            const value = detail !== undefined ? detail : select.value;
            if (detail !== undefined) select.value = detail;
            onValueChange(value);
        };
        const handleBlur = () => onBlur?.();
        select.addEventListener('change', handleChange);
        select.addEventListener('focusout', handleBlur);
        return () => {
            select.removeEventListener('change', handleChange);
            select.removeEventListener('focusout', handleBlur);
            ref?.(null);
        };
    };

/**
 * The subset of a react-hook-form `register()` result (or a plain change/blur pair) that a
 * `<w-select>` needs bridged. RHF's `onChange`/`onBlur` are `ChangeHandler`s that read
 * `event.target.value`, so a native DOM `Event` satisfies them.
 */
type SelectField = {
    ref?: RefFn;
    onChange?: (event: Event) => unknown;
    onBlur?: (event: Event) => unknown;
};

/**
 * Bridge a `<w-select>` web component to React / react-hook-form.
 *
 * `<w-select>` dispatches its `change` as a `CustomEvent` whose `detail` is the newly selected
 * value but does NOT update the host's own `value` property, so RHF (which reads
 * `event.target.value` and the ref's value at submit) would see a stale value — we copy `detail`
 * onto the host before forwarding. Also forwards the element to RHF's ref and cleans everything up
 * (including clearing RHF's ref) on unmount.
 */
export const bindSelectEvents =
    ({ ref, onChange, onBlur }: SelectField) =>
    (el: HTMLElement | null) => {
        ref?.(el);
        if (!el) return;
        const handleChange = (event: Event) => {
            const { detail } = event as CustomEvent<string>;
            if (detail !== undefined) (el as HTMLSelectElement).value = detail;
            onChange?.(event);
        };
        const handleBlur = (event: Event) => onBlur?.(event);
        el.addEventListener('change', handleChange);
        el.addEventListener('focusout', handleBlur);
        return () => {
            el.removeEventListener('change', handleChange);
            el.removeEventListener('focusout', handleBlur);
            ref?.(null);
        };
    };

/**
 * Bridge a `<w-radio>`/`<w-checkbox>` card toggle to React: reflect the controlled `checked`
 * state onto the element and route its native `change` (and optional `focusout`) to callbacks.
 *
 * No outline suppression: the keyboard focus ring is native `:focus-visible` on the control's
 * inner focusable element (`<w-radio>`'s `[tabindex]` wrapper, `<w-checkbox>`'s `<input>`). It
 * only shows for keyboard when the card toggle is a `<div>` + onClick and the heading is plain
 * text instead of a `<label>` (a `<label>` forwarded mouse clicks as focus, showing the ring on
 * click). Suppressing outlines here would kill the keyboard ring too.
 */
export const bindCardEvents =
    (onSelect: () => void, checked: boolean, onBlur?: () => void) =>
    (el: HTMLElement | null) => {
        if (!el) return;
        (el as HTMLInputElement).checked = checked;
        el.toggleAttribute('checked', checked);
        const handleChange = () => onSelect();
        el.addEventListener('change', handleChange);
        const handleBlur = onBlur ? () => onBlur() : undefined;
        if (handleBlur) el.addEventListener('focusout', handleBlur);
        return () => {
            el.removeEventListener('change', handleChange);
            if (handleBlur) el.removeEventListener('focusout', handleBlur);
        };
    };

/**
 * Bridge a standalone `<w-checkbox>` that slots its own label (a migrated Warp
 * `<Toggle type="checkbox" />`) to React: reflect the controlled `checked` state onto the element
 * and route its native `change` (and optional `focusout`) to callbacks. Unlike `bindCardEvents`
 * (arg-less `onSelect()` for card selection), `onChange` receives the checkbox's new checked state
 * as a boolean — read from the reflected `checked` attribute, not the `.checked` property: the real
 * `<w-checkbox>` reflects checked→attribute before dispatching `change`, and the jsdom test stub
 * only toggles the attribute, so this stays correct in both environments. Matches the single-option
 * contract of the old Warp `<Toggle>`.
 */
export const bindCheckboxEvents =
    (onChange: (checked: boolean) => void, checked: boolean, onBlur?: () => void) =>
    (el: HTMLElement | null) => {
        if (!el) return;
        (el as HTMLInputElement).checked = checked;
        el.toggleAttribute('checked', checked);
        const handleChange = () => onChange(el.hasAttribute('checked'));
        el.addEventListener('change', handleChange);
        const handleBlur = onBlur ? () => onBlur() : undefined;
        if (handleBlur) el.addEventListener('focusout', handleBlur);
        return () => {
            el.removeEventListener('change', handleChange);
            if (handleBlur) el.removeEventListener('focusout', handleBlur);
        };
    };
